"use client";

import type { VariantProps } from "class-variance-authority";
import { cn } from "cn";
import * as React from "react";
import { inputVariants } from "./Input";

type AutocompleteGroup = {
  /** Shown above the group's suggestions; omit it for an unlabelled list. */
  label?: string;
  items: string[];
};

interface AutocompleteProps
  extends Omit<
      React.ComponentProps<"input">,
      "value" | "onChange" | "onSelect" | "size"
    >,
    Pick<VariantProps<typeof inputVariants>, "size"> {
  value: string;
  onValueChange: (value: string) => void;
  suggestions: AutocompleteGroup[];
  /** Called after a suggestion is picked and written into the input. */
  onSelect?: (value: string) => void;
  listClassName?: string;
}

const matches = (item: string, query: string) =>
  item.toLowerCase().includes(query.trim().toLowerCase());

/**
 * A free-text input that suggests values as the user types. Unlike a
 * Combobox, the typed text is the value: suggestions are shortcuts, never a
 * constraint.
 *
 * Nothing is highlighted until the user reaches for the list, so Enter keeps
 * what was typed unless a suggestion was chosen on purpose. `onKeyDown` only
 * receives the keys the list did not take: an Escape that closes the list or
 * an Enter that picks a suggestion never reach it.
 */
function Autocomplete({
  value,
  onValueChange,
  suggestions,
  onSelect,
  className,
  listClassName,
  size,
  onKeyDown,
  onClick,
  onBlur,
  id,
  ...props
}: AutocompleteProps) {
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  const listId = `${inputId}-list`;
  const optionId = (index: number) => `${inputId}-option-${index}`;

  const [open, setOpen] = React.useState(false);
  const [highlighted, setHighlighted] = React.useState(-1);

  const groups = suggestions
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => matches(item, value)),
    }))
    .filter((group) => group.items.length > 0);
  const options = groups.flatMap((group) => group.items);
  const expanded = open && options.length > 0;

  const close = () => {
    setOpen(false);
    setHighlighted(-1);
  };

  const select = (option: string) => {
    onValueChange(option);
    close();
    onSelect?.(option);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        setOpen(true);
        setHighlighted((index) => Math.min(index + 1, options.length - 1));
        return;
      }
      case "ArrowUp": {
        event.preventDefault();
        setOpen(true);
        // Moving up from the first suggestion returns to the typed text.
        setHighlighted((index) => (index < 0 ? options.length - 1 : index - 1));
        return;
      }
      case "Enter": {
        const option = options[highlighted];
        if (expanded && option !== undefined) {
          event.preventDefault();
          select(option);
          return;
        }
        break;
      }
      case "Escape": {
        if (expanded) {
          event.preventDefault();
          close();
          return;
        }
        break;
      }
    }
    onKeyDown?.(event);
  };

  let offset = 0;

  return (
    <div data-slot="autocomplete" className="relative">
      <input
        id={inputId}
        role="combobox"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          expanded && highlighted >= 0 ? optionId(highlighted) : undefined
        }
        autoComplete="off"
        value={value}
        onChange={(event) => {
          onValueChange(event.target.value);
          setOpen(true);
          setHighlighted(-1);
        }}
        onClick={(event) => {
          onClick?.(event);
          setOpen(true);
        }}
        onBlur={(event) => {
          close();
          onBlur?.(event);
        }}
        onKeyDown={handleKeyDown}
        className={cn(inputVariants({ size }), className)}
        {...props}
      />
      <div
        id={listId}
        role="listbox"
        hidden={!expanded}
        // Keeps focus in the input, which would otherwise blur and close the
        // list before a click on a suggestion lands.
        onMouseDown={(event) => event.preventDefault()}
        className={cn(
          "absolute top-full left-0 z-50 mt-1 w-full",
          "max-h-72 overflow-y-auto overscroll-contain",
          "rounded-md border border-border bg-card p-1 shadow-lg",
          listClassName,
        )}
      >
        {groups.map((group, groupIndex) => {
          const start = offset;
          offset += group.items.length;
          const labelId = `${inputId}-group-${groupIndex}`;
          return (
            // biome-ignore lint/a11y/useSemanticElements: a listbox groups its options with role="group"; a fieldset is not allowed inside it
            <div
              key={group.label ?? groupIndex}
              role="group"
              aria-labelledby={group.label ? labelId : undefined}
            >
              {group.label && (
                <div
                  id={labelId}
                  className="px-2 py-1 caption-md text-muted-foreground"
                >
                  {group.label}
                </div>
              )}
              {group.items.map((item, itemIndex) => {
                const index = start + itemIndex;
                return (
                  // biome-ignore lint/a11y/useKeyWithClickEvents: options are reached with the arrow keys from the input
                  <div
                    key={item}
                    id={optionId(index)}
                    role="option"
                    tabIndex={-1}
                    aria-selected={index === highlighted}
                    onPointerMove={() => setHighlighted(index)}
                    onClick={() => select(item)}
                    className={cn(
                      "flex cursor-pointer items-center rounded-sm px-2 py-2",
                      "body-md text-foreground select-none",
                      "aria-selected:bg-accent",
                    )}
                  >
                    {item}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { Autocomplete, type AutocompleteGroup, type AutocompleteProps };
